import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDownRight } from "lucide-react";
import { MaskLines } from "@/components/Reveal";
import { CorridorOrb } from "@/components/landing/CorridorOrb";

const fade = (d) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, delay: d, ease: [0.22, 1, 0.36, 1] } });

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const orbScale = useTransform(scrollYProgress, [0, 1], [1, 0.86]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
      <div className="grid-lines absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_60%_40%,black_20%,transparent_75%)]" />
      <div className="absolute -right-40 top-10 h-[520px] w-[520px] rounded-full bg-[#0066FF]/20 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-10 px-5 sm:px-10 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div {...fade(0)} className="mb-8 flex flex-wrap items-center gap-3">
            <span className="eyebrow">US ISO Merchant Banking</span>
            <span className="h-px w-8 bg-white/25" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">FinBPM Alliance · US Desk</span>
          </motion.div>
          <h1 className="font-display text-[13vw] font-semibold leading-[0.92] tracking-[-0.04em] sm:text-7xl lg:text-[6.6rem]" data-testid="hero-headline">
            <MaskLines lines={["Approve the", { text: "unapprovable.", className: "text-[var(--gold)] italic font-light" }, "Settle anywhere."]} />
          </h1>
          <motion.p {...fade(0.75)} className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
            MidHq is a high-risk focused processor with our own banks and access to <span className="text-white">135 banks in 29 countries</span>, including Mexico, Bulgaria and Amsterdam. Together with FinBPM, we tag-team cross-border payments from the US outward.
          </motion.p>
          <motion.div {...fade(0.9)} className="mt-10 flex flex-wrap gap-3">
            <Link to="/apply" className="btn-primary" data-testid="hero-apply-button">Apply for processing <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/store" className="btn-ghost" data-testid="hero-store-button">Browse the storefront</Link>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: orbY, scale: orbScale }} className="lg:col-span-5">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <CorridorOrb />
          </motion.div>
        </motion.div>
      </div>
      <motion.div {...fade(1.2)} className="relative mx-auto mt-12 flex max-w-[1400px] items-end justify-between px-5 pb-10 sm:px-10">
        <div className="flex gap-10 font-mono text-xs text-slate-400">
          <div><p className="text-2xl text-white">135</p><p>partner banks</p></div>
          <div><p className="text-2xl text-white">29</p><p>countries</p></div>
          <div className="hidden sm:block"><p className="text-2xl text-white">US ⇄ EU ⇄ LATAM</p><p>settlement lanes</p></div>
        </div>
        <span className="hidden items-center gap-2 font-mono text-xs text-slate-400 sm:flex">Scroll <ArrowDownRight className="h-4 w-4" /></span>
      </motion.div>
    </section>
  );
}
